/* ------------------------------------------------------------------
   Title-based search (Little Luxuries)

   - Main /search page: "velvet cushion" becomes
     "title:velvet* AND title:cushion*" when the search form is submitted.
   - The customer still sees the words they typed, not the internal syntax.
   - Plural endings are normalised (candles -> candle).
   - SYNONYMS widens a word to closely related products. Every entry was
     checked against the catalogue and has active products behind it.
   - EXCLUDE stops a word matching look-alike words (tea is not teak).
   - Safety net: if the precise search finds fewer than MIN_RESULTS products,
     the page reloads once with Shopify's standard search and says so.
   - The dropdown (predictive search) uses the same rules.
   ------------------------------------------------------------------ */
(function () {
  if (window.titleSearch) return;

  /* A key is a lower case, singular word or phrase. Each value lists words
     that should ALSO be searched. Each item can be:
       'word'                       a single word
       ['side', 'table']            all words must appear in the title
       { all: [...], not: [...] }   all words must appear, none of the 'not'
                                    words may appear
     Only the typed words are expanded; nothing chains. */
  var SYNONYMS = {
    desk: ['console', ['side', 'table']],
    seat: ['chair', 'stool'],
    seating: ['chair', 'stool'],
    cupboard: ['cabinet'],
    hamper: ['basket'],
    storage: ['basket', 'jar', 'canister', 'box'],
    hanger: ['hook'],

    /* Jars: round, lidded containers. Boxes and tins (wood, metal, tin, box in
       the title) are not jars, so lidded storage is only included when it is
       not one of those. */
    jar: [
      'canister',
      { all: ['storage', 'lid'], not: ['wood', 'metal', 'tin', 'box'] },
      ['storage', 'stoneware'],
      ['sugar', 'pot'],
      ['cellar', 'lid']
    ],
    canister: ['jar'],
    container: ['jar', 'canister', ['storage', 'lid']],

    pillow: ['cushion'],
    cushion: ['pillow'],
    carpet: ['rug'],
    blanket: ['quilt', 'bedcover'],
    throw: ['quilt', 'bedcover'],
    comforter: ['quilt', 'bedcover'],
    duvet: ['quilt', 'bedcover'],
    bedspread: ['quilt', 'bedcover'],
    bedding: ['quilt', 'bedcover'],
    drape: ['curtain'],
    bathroom: ['bath', 'towel'],

    vase: ['budvase'],
    budvase: ['vase'],
    'bud vase': ['budvase'],
    planter: ['flowerpot', ['flower', 'pot']],
    flowerpot: [['flower', 'pot'], 'planter'],
    'flower pot': ['flowerpot', 'planter'],
    'plant pot': ['flowerpot', 'planter', ['flower', 'pot']],

    pitcher: ['jug', 'carafe'],
    jug: ['pitcher', 'carafe'],
    carafe: ['jug', 'pitcher'],
    flatware: ['cutlery'],
    silverware: ['cutlery'],
    serviette: ['napkin'],
    placemat: [['table', 'mat']],
    'place mat': ['placemat', ['table', 'mat']],
    'table mat': ['placemat'],
    tablecloth: [['table', 'cloth']],
    'table cloth': ['tablecloth'],
    teacup: [['tea', 'cup']],
    cakestand: [['cake', 'stand']],
    tableware: ['plate', 'bowl', 'cup', 'mug'],
    dinnerware: ['tableware', 'plate', 'bowl'],
    drinkware: ['mug', 'cup'],
    serveware: ['serving', 'platter', 'tray', ['cake', 'stand']],

    candlestick: [['candle', 'holder']],
    candleholder: [['candle', 'holder'], 'candlestick'],
    'candle holder': ['candlestick'],
    'candle stand': [['candle', 'holder'], 'candlestick'],
    lighting: ['lamp'],

    jewellery: ['jewelry'],
    jewelry: ['jewellery'],
    grey: ['gray'],
    gray: ['grey'],
    organiser: ['organizer'],
    organizer: ['organiser'],
    aluminum: ['aluminium'],
    aluminium: ['aluminum'],
    wooden: ['wood'],
    metallic: ['metal']
  };

  /* When this is the ONLY word typed, search these instead of the word itself
     ("light" means lamps, not the colour Light Blue). */
  var REPLACE = {
    light: ['lamp']
  };

  /* Words that must not match look-alikes. */
  var EXCLUDE = {
    tea: ['teak'],
    cup: ['cupcake', 'cupboard']
  };

  var IRREGULAR = { shelves: 'shelf', knives: 'knife', leaves: 'leaf', loaves: 'loaf' };
  var MAX_ALTERNATIVES = 4;

  /* Safety net: fewer precise matches than this triggers one wider search */
  var MIN_RESULTS = 6;

  function lookup(map, key) {
    return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;
  }

  function singular(word) {
    if (word.length < 4 || /(ss|us|is)$/.test(word)) return word;
    if (/(ches|shes|xes|sses)$/.test(word)) return word.slice(0, -2);
    if (/s$/.test(word)) return word.slice(0, -1);
    return word;
  }

  function tokenize(raw) {
    return (raw || '')
      .toLowerCase()
      .replace(/['’‘]s\b/g, '')
      .replace(/['’‘`]/g, '')
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean);
  }

  function normalise(raw) {
    return tokenize(raw).map(function (word) { return lookup(IRREGULAR, word) || singular(word); });
  }

  function asArray(value) {
    return [].concat(value);
  }

  /* Turn any synonym item into { all: [...], not: [...] } */
  function parseAlt(alt) {
    if (alt && typeof alt === 'object' && !Array.isArray(alt)) {
      return { all: asArray(alt.all), not: asArray(alt.not || []) };
    }
    return { all: asArray(alt), not: [] };
  }

  function termFor(word) {
    var term = 'title:' + word + '*';
    var excluded = lookup(EXCLUDE, word);
    if (!excluded) return term;
    return '(' + [term].concat(excluded.map(function (item) { return 'NOT title:' + item + '*'; })).join(' AND ') + ')';
  }

  function altQuery(alt) {
    var parsed = parseAlt(alt);
    var terms = parsed.all.map(termFor).concat(parsed.not.map(function (word) { return 'NOT title:' + word + '*'; }));
    var query = terms.join(' AND ');
    return terms.length > 1 ? '(' + query + ')' : query;
  }

  function groupQuery(words, alts) {
    var literal = words.map(termFor).join(' AND ');
    if (words.length > 1) literal = '(' + literal + ')';
    var terms = [literal].concat(alts.map(altQuery));
    return terms.length > 1 ? '(' + terms.join(' OR ') + ')' : terms[0];
  }

  /* Main results page query, or null when the query should be left as typed */
  function toTitleQuery(raw) {
    var value = (raw || '').trim();
    if (!value || /[:"*()]/.test(value) || /\b(AND|OR|NOT)\b/.test(value)) return null;

    var words = normalise(value);
    if (!words.length) return null;

    var replacement = words.length === 1 ? lookup(REPLACE, words[0]) : null;
    if (replacement) {
      var replaced = replacement.map(altQuery);
      return replaced.length > 1 ? '(' + replaced.join(' OR ') + ')' : replaced[0];
    }

    var parts = [];
    var index = 0;
    while (index < words.length) {
      var size = Math.min(3, words.length - index);
      var alts = null;
      for (; size > 0; size--) {
        alts = lookup(SYNONYMS, words.slice(index, index + size).join(' '));
        if (alts) break;
      }

      if (alts) {
        parts.push(groupQuery(words.slice(index, index + size), alts));
        index += size;
      } else {
        parts.push(termFor(words[index]));
        index += 1;
      }
    }

    return parts.join(' AND ');
  }

  /* Dropdown plan: whether to search the typed words, and which extra word lists */
  function plan(raw) {
    var words = normalise(raw);
    if (!words.length) return { literal: true, alts: [] };

    var replacement = words.length === 1 ? lookup(REPLACE, words[0]) : null;
    if (replacement) return { literal: false, alts: replacement.map(parseAlt).slice(0, MAX_ALTERNATIVES) };

    var alts = lookup(SYNONYMS, words.join(' '));
    return { literal: true, alts: (alts || []).map(parseAlt).slice(0, MAX_ALTERNATIVES) };
  }

  function containsWords(text, words) {
    var tokens = tokenize(text).map(singular);
    return words.every(function (word) { return tokens.indexOf(word) !== -1; });
  }

  /* True when the text contains a word starting with any of the given words */
  function hasAny(text, words) {
    var tokens = tokenize(text).map(singular);
    return tokens.some(function (token) {
      return words.some(function (word) { return token.indexOf(word) === 0; });
    });
  }

  /* True when the title only matches through a look-alike word (teak for tea) */
  function violates(text, words) {
    var tokens = null;
    return words.some(function (word) {
      var excluded = lookup(EXCLUDE, word);
      if (!excluded) return false;
      tokens = tokens || tokenize(text).map(singular);
      if (tokens.indexOf(word) !== -1) return false;
      return tokens.some(function (token) {
        return excluded.some(function (item) { return token.indexOf(item) === 0; });
      });
    });
  }

  window.titleSearch = {
    normalise: normalise,
    plan: plan,
    containsWords: containsWords,
    hasAny: hasAny,
    violates: violates
  };

  /* ---------- Show the customer's own words, never the internal syntax ---------- */

  function storageKey(query) {
    return 'titleSearch:' + query;
  }

  function remember(query, original) {
    try { sessionStorage.setItem(storageKey(query), original); } catch (error) { /* ignore */ }
  }

  function recall(query) {
    try { return sessionStorage.getItem(storageKey(query)); } catch (error) { return null; }
  }

  function splitTop(text, separator) {
    var parts = [];
    var depth = 0;
    var start = 0;
    for (var i = 0; i < text.length; i++) {
      var character = text.charAt(i);
      if (character === '(') depth++;
      else if (character === ')') depth--;
      else if (depth === 0 && text.substr(i, separator.length) === separator) {
        parts.push(text.slice(start, i));
        start = i + separator.length;
        i = start - 1;
      }
    }
    parts.push(text.slice(start));
    return parts;
  }

  function isWrapped(text) {
    if (text.charAt(0) !== '(' || text.charAt(text.length - 1) !== ')') return false;
    var depth = 0;
    for (var i = 0; i < text.length; i++) {
      if (text.charAt(i) === '(') depth++;
      else if (text.charAt(i) === ')') {
        depth--;
        if (depth === 0 && i < text.length - 1) return false;
      }
    }
    return true;
  }

  function partWords(part) {
    part = part.trim();
    while (isWrapped(part)) part = part.slice(1, -1).trim();

    var first = splitTop(part, ' OR ')[0].replace(/NOT\s+title:[^\s()]+/g, '');
    var words = [];
    first.replace(/title:([^\s*()]+)/g, function (match, word) { words.push(word); return match; });
    return words;
  }

  /* Fallback for shared links: rebuild the typed words from the query itself */
  function deriveTerms(query) {
    var words = [];
    splitTop(query, ' AND ').forEach(function (part) {
      if (/^NOT\s/.test(part.trim())) return;
      words = words.concat(partWords(part));
    });
    return words.join(' ');
  }

  var rawQuery = new URLSearchParams(window.location.search).get('q') || '';

  function displayTerms() {
    if (!/title:/.test(rawQuery)) return null;
    return recall(rawQuery) || deriveTerms(rawQuery) || null;
  }

  function genericClean(text) {
    return text.replace(/title:/gi, '').replace(/\*/g, '').replace(/\s+AND\s+/g, ' ');
  }

  function replaceTerms(text, display) {
    if (display) {
      [rawQuery, rawQuery.replace(/\*/g, '')].forEach(function (needle) {
        if (needle && text.indexOf(needle) !== -1) text = text.split(needle).join(display);
      });
    }
    return /title:/i.test(text) ? genericClean(text) : text;
  }

  function cleanTextNodes(root, display) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      var parent = node.parentNode && node.parentNode.nodeName;
      if (parent === 'SCRIPT' || parent === 'STYLE') continue;
      if (/title:/i.test(node.nodeValue)) node.nodeValue = replaceTerms(node.nodeValue, display);
    }
  }

  /* ---------- Safety net: very few precise matches -> one wider search ---------- */

  function isRefined(params) {
    var refined = false;
    params.forEach(function (value, key) {
      if (/^filter\./.test(key) || key === 'sort_by' || key === 'page') refined = true;
    });
    return refined;
  }

  function showWiderResults(root, display) {
    if (!display || !/title:/.test(rawQuery)) return false;

    var params = new URLSearchParams(window.location.search);
    if (params.get('related') === '1' || isRefined(params)) return false;

    var grid = root.querySelector('.card-grid');
    var count = grid ? grid.children.length : 0;
    if (count >= MIN_RESULTS) return false;

    params.set('q', display);
    params.set('related', '1');
    window.location.replace(window.location.pathname + '?' + params.toString());
    return true;
  }

  function addRelatedNote(root) {
    if (new URLSearchParams(window.location.search).get('related') !== '1') return;

    var header = root.querySelector('.title-wrapper');
    if (!header || root.querySelector('.search-related-note')) return;

    var note = document.createElement('p');
    note.className = 'search-related-note text-center';
    note.textContent = 'We found only a few exact matches, so these results are wider.';
    header.insertAdjacentElement('afterend', note);
  }

  function watchResults() {
    var display = displayTerms();
    var root = document.querySelector('.search-section');

    if (root && showWiderResults(root, display)) return;

    if (/title:/i.test(document.title)) document.title = replaceTerms(document.title, display);
    if (!root) return;

    addRelatedNote(root);

    if (display) {
      Array.prototype.forEach.call(root.querySelectorAll('input[type="search"]'), function (input) {
        input.value = display;
      });
    }

    cleanTextNodes(root, display);
    new MutationObserver(function () { cleanTextNodes(root, display); })
      .observe(root, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchResults);
  else watchResults();

  /* ---------- Rewrite the query when the search form is submitted ---------- */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/search"]')) return;

    var input = form.querySelector('input[name="q"]');
    if (!input) return;

    var query = toTitleQuery(input.value);
    if (!query) return;

    remember(query, input.value.trim());

    var hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'q';
    hidden.value = query;

    input.removeAttribute('name');
    form.appendChild(hidden);

    setTimeout(function () {
      input.setAttribute('name', 'q');
      hidden.remove();
    }, 0);
  }, true);
})();

if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl(query) {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', query === undefined ? this.getQuery() : query);
        /* Title-only search: restrict predictive results to product titles */
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[options][fields]', 'title');
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      /* The typed query (unless replaced), plus any synonym searches */
      getVariants() {
        const query = this.getQuery();
        const plan = window.titleSearch ? window.titleSearch.plan(query) : { literal: true, alts: [] };
        const variants = [];

        if (plan.literal) variants.push({ url: this.buildUrl(query).toString(), words: null, not: [] });
        plan.alts.forEach((alt) => {
          variants.push({ url: this.buildUrl(alt.all.join(' ')).toString(), words: alt.all, not: alt.not });
        });

        return variants;
      }

      /* Combine the product cards from every variant, removing duplicates,
         requiring synonym matches to contain all their words (and none of
         their excluded words), and dropping look-alike matches such as teak
         for tea */
      mergeResponses(texts, variants) {
        if (!window.titleSearch) return texts[0];

        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const parser = new DOMParser();
        const docs = texts.map((text) => parser.parseFromString(text, 'text/html'));
        const limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3;
        const literalWords = window.titleSearch.normalise(this.getQuery());
        const seen = new Set();
        const cards = [];

        docs.forEach((doc, index) => {
          const container = doc.getElementById(id);
          const grid = container && container.querySelector('.horizontal-products');
          if (!grid) return;

          const words = variants[index].words;
          const excludedWords = variants[index].not;
          Array.from(grid.children).forEach((card) => {
            const link = card.querySelector('a[href]');
            const key = link ? link.getAttribute('href').split('?')[0] : card.textContent.trim();
            if (seen.has(key)) return;

            const text = card.textContent;
            if (words && !window.titleSearch.containsWords(text, words)) return;
            if (excludedWords && excludedWords.length && window.titleSearch.hasAny(text, excludedWords)) return;
            if (window.titleSearch.violates(text, words || literalWords)) return;

            seen.add(key);
            cards.push(card);
          });
        });

        if (cards.length === 0) return texts[0];

        const baseDoc = docs.find((doc) => {
          const container = doc.getElementById(id);
          return container && container.querySelector('.horizontal-products');
        });
        const baseGrid = baseDoc.getElementById(id).querySelector('.horizontal-products');

        baseGrid.innerHTML = '';
        cards.slice(0, limit).forEach((card) => baseGrid.appendChild(baseDoc.importNode(card, true)));

        return baseDoc.documentElement.outerHTML;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.setAttribute('loading', '');

        const variants = this.getVariants();

        Promise.all(variants.map((variant) => fetch(variant.url, { signal }).then((response) => response.text())))
          .then((texts) => {
            const responseText = this.mergeResponses(texts, variants);
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        document.getElementById(id).innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
